import { Box, Stack, Tab, Tabs, Typography } from "@mui/material";
import { SyntheticEvent, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  PatientHistoryTab,
  PatientHeadacheDiaryTab,
  PatientDetailsTab,
  TabPanel,
  PatientStatisticsTab,
  HistoryQuestionsProvider,
} from "../components";
import { Attack, History, Menstruation, PatientDetails } from "../types";
import * as apiClient from "../api";
import { byStartDate, byStartDateTime } from "../helpers/datetimes";

const a11yProps = (index: number) => {
  return {
    id: `profile-tab-${index}`,
    "aria-controls": `profile-tabpanel-${index}`,
  };
};

const PatientProfile = () => {
  const { patientId } = useParams();
  const [loaded, setLoaded] = useState(false);
  const [patient, setPatient] = useState<PatientDetails>({
    email: "",
    firstName: "",
    lastName: "",
    gender: "male",
    dob: "",
    phone: "",
  });
  const [histories, setHistories] = useState<History[]>([]);
  const [historiesLoaded, setHistoriesLoaded] = useState(false);
  const [attacks, setAttacks] = useState<Attack[]>([]);
  const [attacksLoaded, setAttacksLoaded] = useState(false);
  const [menstruations, setMenstruations] = useState<Menstruation[]>([]);
  const [menstruationsLoaded, setMenstruationsLoaded] = useState(false);
  const [tabIdx, setTabIdx] = useState(0);

  useEffect(() => {
    const loadPatient = async () => {
      const res = await apiClient.getPatient(patientId!);
      setPatient(res.data as PatientDetails);
      setLoaded(true);
    };
    const loadHistories = async () => {
      const res = await apiClient.getPatientHistories(patientId!);
      setHistories(res.data.histories as History[]);
      setHistoriesLoaded(true);
    };
    const loadAttacks = async () => {
      const res = await apiClient.getAttacks(patientId!);
      const sorted = res.data.attacks.sort(byStartDateTime);
      setAttacks(sorted);
      setAttacksLoaded(true);
    };
    const loadMenstruations = async () => {
      const res = await apiClient.getMenstruations(patientId!);
      const sorted = res.data.menstruations.sort(byStartDate);
      setMenstruations(sorted);
      setMenstruationsLoaded(true);
    };

    loadPatient();
    loadHistories();
    loadAttacks();
    loadMenstruations();
  }, [patientId]);

  const handleChange = (_: SyntheticEvent, newValue: number) => {
    setTabIdx(newValue);
  };

  return (
    <HistoryQuestionsProvider>
      <Stack spacing={2}>
        <Typography variant="h4">
          {patient.firstName} {patient.lastName}
        </Typography>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabIdx}
            onChange={handleChange}
            aria-label="profile tabs"
          >
            <Tab label="Details" {...a11yProps(0)} />
            <Tab label="History" {...a11yProps(1)} />
            <Tab label="Headache Diary" {...a11yProps(2)} />
            <Tab label="Statistics" {...a11yProps(3)} />
          </Tabs>
        </Box>
        <TabPanel value={tabIdx} index={0} loaded={loaded}>
          <PatientDetailsTab
            patientId={patientId!}
            patientDetails={patient}
            setPatient={setPatient}
          />
        </TabPanel>
        <TabPanel value={tabIdx} index={1} loaded={loaded}>
          <PatientHistoryTab
            patientId={patientId!}
            histories={histories}
            setHistories={setHistories}
            loaded={historiesLoaded}
          />
        </TabPanel>
        <TabPanel value={tabIdx} index={2} loaded={loaded}>
          <PatientHeadacheDiaryTab
            patientId={patientId!}
            attacks={attacks}
            setAttacks={setAttacks}
            attacksLoaded={attacksLoaded}
            menstruations={menstruations}
            setMenstruations={setMenstruations}
            menstruationsLoaded={menstruationsLoaded}
          />
        </TabPanel>
        <TabPanel value={tabIdx} index={3} loaded={loaded}>
          <PatientStatisticsTab
            patientDetails={patient}
            attacks={attacks}
            attacksLoaded={attacksLoaded}
          />
        </TabPanel>
      </Stack>
    </HistoryQuestionsProvider>
  );
};

export default PatientProfile;
